import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchRoyaleMe, fetchRoyaleHistory, ROYALE_REWARDS, type RoyaleMe, type RoyaleHistoryRow, type CrownReward } from "../../shared/royale";
import { eggIconUrl } from "../../shared/golden-eggs";
import styles from "./RoyaleBlock.module.scss";

// The Royale on your own profile: the wins counter, what you have played, the
// five win rewards (hexagons: a reward reached is claimed in the Store, where
// its Golden Eggs and item are granted; RoyaleClaim.tsx) and your recent
// Royales.
//
// Every read comes back empty when it fails or the tables are not there yet
// (shared/royale.ts), so this block has no error state: it just shows a player
// who has not played a Royale.

const SHOWN = 8;

export function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return n + "th";
  return n + (["th", "st", "nd", "rd"][n % 10 < 4 ? n % 10 : 0] || "th");
}

// "2 h ago", "Yesterday", "3 days ago", then the date.
export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 2) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

const Crown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 8l4 3.5L12 5l4 6.5L20 8l-1.6 10H5.6L4 8z" fill="currentColor" fillOpacity="0.25" /><path d="M6 21h12" />
  </svg>
);
const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
);

type RewardState = "locked" | "ready" | "opened";

export default function RoyaleBlock() {
  // undefined = loading; null = nothing on record (or it could not be read).
  const [me, setMe] = useState<RoyaleMe | null | undefined>(undefined);
  const [history, setHistory] = useState<RoyaleHistoryRow[] | undefined>(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    let live = true;
    fetchRoyaleMe().then((r) => { if (live) setMe(r); });
    fetchRoyaleHistory(SHOWN).then((h) => { if (live) setHistory(h); });
    return () => { live = false; };
  }, []);

  const stateOf = (r: CrownReward): RewardState => {
    if (me?.opened?.includes(r.id)) return "opened";
    if (me?.openable?.includes(r.id)) return "ready";
    return "locked";
  };
  // A reward reached is claimed in the Store (its item's shelf when it brings one).
  const claim = (r: CrownReward) => navigate(r.item && r.item.kind === "head" ? "/shop?tab=heads" : "/shop");

  const wins = me?.wins ?? 0;
  const played = me?.played ?? 0;
  const loading = me === undefined;

  return (
    <section className={styles.block} aria-label="Royale">
      <div className={styles.inner}>
      <div className={styles.summary}>
        <span className={styles.crown}><Crown /></span>
        <span className={styles.summaryText}>
          <small>Royale wins</small>
          <em>
            {loading ? " "
              : played ? `Played ${played}${me?.bestPlace ? ` · Best ${ordinal(me.bestPlace)}` : ""}`
              : "No Royale played yet"}
          </em>
        </span>
        <b className={styles.wins}>{loading ? "" : wins}</b>
      </div>

      <div className={styles.rewards}>
        <div className={styles.hexRow}>
          {ROYALE_REWARDS.map((r) => {
            const st = stateOf(r);
            const cls = st === "locked" ? styles.locked : st === "opened" ? styles.opened : styles.ready;
            return (
              <button
                key={r.id}
                type="button"
                className={`${styles.reward} ${cls}`}
                disabled={st !== "ready"}
                onClick={() => claim(r)}
                data-sound="none"
                aria-label={
                  st === "ready" ? `Claim the reward for ${r.wins} ${r.wins === 1 ? "win" : "wins"} in the Store`
                    : st === "opened" ? `Reward for ${r.wins} ${r.wins === 1 ? "win" : "wins"}, opened`
                    : `Reward at ${r.wins} ${r.wins === 1 ? "win" : "wins"}`
                }
              >
                <span className={styles.hex}>
                  <i />
                  {st === "locked" ? <span className={styles.need}><b>{r.wins}</b>{r.wins === 1 ? "win" : "wins"}</span>
                    : st === "opened" ? <span className={styles.done}><Check /></span>
                    : <span className={styles.seal}><img src={eggIconUrl(44)} alt="" /></span>}
                </span>
                <span className={styles.rewardCap}>
                  {st === "ready" ? "Claim" : `${r.eggs} eggs`}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.history}>
        <div className={styles.historyHead}>Recent Royales</div>
        {history === undefined ? (
          <div className={styles.historyEmpty}>&nbsp;</div>
        ) : history.length === 0 ? (
          <div className={styles.historyEmpty}>None yet</div>
        ) : (
          <ul className={styles.historyList}>
            {history.map((h, i) => (
              <li key={h.number + ":" + i} className={h.place === 1 ? `${styles.historyRow} ${styles.historyWin}` : styles.historyRow}>
                <i />
                <b>Royale #{h.number}</b>
                <span>{h.place === 1 ? "Winner" : ordinal(h.place)} of {h.size}</span>
                <small>{timeAgo(h.at)}</small>
                {h.place === 1 ? <strong><Crown /></strong> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      </div>
    </section>
  );
}
